import React, { useState } from 'react';
import CustomSVGs from '../customSVGs/CustomSVGs';
import {
  calendarSVG,
  clockSVG,
  crossSmallSVG,
  playSVG,
  tickSVG,
  trendLineSVG,
} from '../../lib/allCustomSVGs';
import '../../styles/cards/aiOptimizationsManualCard.scss';
import { useTranslation } from 'react-i18next';

interface Props {
  icon: any;
  title: string;
  description?: string;
  priority: 'high' | 'medium' | 'low';
  type: 'conversion' | 'performance' | 'accessibility';
  rateMin: number;
  rateMax: number;
  effort: 'low' | 'medium' | 'high';
  status: 'Pending' | 'Snoozed' | 'Done' | 'Dismissed';
  date: string;
  snoozedUntil?: string;
  onDoneClick?: () => void;
  onSnoozeClick?: () => void;
  onDismissClick?: () => void;
  onResumeClick?: () => void;
}

const AIOptimizationsManualCard: React.FC<Props> = ({
  icon,
  title,
  description,
  priority,
  type,
  rateMin,
  rateMax,
  effort,
  status,
  date,
  snoozedUntil,
  onDoneClick,
  onSnoozeClick,
  onDismissClick,
  onResumeClick,
}) => {
  const { t } = useTranslation();

  const renderButtons = () => {
    switch (status) {
      case 'Done':
        return (
          <div className='aiOptimizationsManualCard__info--config-completedTag'>
            <CustomSVGs svg={tickSVG} />
            <span>{t('Completed')}</span>
          </div>
        );

      case 'Dismissed':
        return (
          <div className='aiOptimizationsManualCard__info--config-dismissedTag'>
            <CustomSVGs svg={crossSmallSVG} />
            <span>{t('Dismissed')}</span>
          </div>
        );

      case 'Pending':
        return (
          <>
            <div
              className='aiOptimizationsManualCard__info--config-btn'
              onClick={onDoneClick}
            >
              <CustomSVGs svg={tickSVG} />
              <span>{t('Done')}</span>
            </div>
            <div
              className='aiOptimizationsManualCard__info--config-btn'
              onClick={onSnoozeClick}
            >
              <CustomSVGs svg={clockSVG} />
              <span>{t('Snooze')}</span>
            </div>
            <div
              className='aiOptimizationsManualCard__info--config-btn aiOptimizationsManualCard__info--config-btn-dismiss'
              onClick={onDismissClick}
            >
              <CustomSVGs svg={crossSmallSVG} />
              <span>{t('Dismiss')}</span>
            </div>
          </>
        );

      case 'Snoozed':
        return (
          <>
            <div
              className='aiOptimizationsManualCard__info--config-btn'
              onClick={onResumeClick}
            >
              <CustomSVGs svg={playSVG} />
              <span>{t('Resume')}</span>
            </div>
            <div
              className='aiOptimizationsManualCard__info--config-btn aiOptimizationsManualCard__info--config-btn-dismiss'
              onClick={onDismissClick}
            >
              <CustomSVGs svg={crossSmallSVG} />
              <span>{t('Dismiss')}</span>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className='aiOptimizationsManualCard'>
      {/* Top Info Section */}
      <div className='aiOptimizationsManualCard__info'>
        <div className='aiOptimizationsManualCard__info--content'>
          <div className='aiOptimizationsManualCard__info--content-icon'>
            <CustomSVGs svg={icon} />
          </div>
          <div className='aiOptimizationsManualCard__info--content-titles'>
            <div className='aiOptimizationsManualCard__info--content-titles-main'>
              <h3>{title}</h3>
              <div
                className={`aiOptimizationsManualCard__info--content-titles-main-priority aiOptimizationsManualCard__info--content-titles-main-priority-${priority}`}
              >
                <span>{t('Priority', { priority })}</span>
              </div>
              <div className='aiOptimizationsManualCard__info--content-titles-main-type'>
                <span>{type}</span>
              </div>
            </div>
            <div className='aiOptimizationsManualCard__info--content-titles-description'>
              {description && <p>{description}</p>}
            </div>
            <div className='aiOptimizationsManualCard__info--content-titles-stats'>
              <div
                className={`aiOptimizationsManualCard__info--content-titles-stats-rates aiOptimizationsManualCard__info--content-titles-stats-rates-${
                  rateMax < 0 ? 'decrease' : 'increase'
                }`}
              >
                <CustomSVGs svg={trendLineSVG} />
                <span>{`${rateMin} - ${rateMax}% ${
                  rateMax < 0 ? 'decrease' : 'increase'
                } in ${type} rate`}</span>
              </div>
              <div className='aiOptimizationsManualCard__info--content-titles-stats-effort'>
                <CustomSVGs svg={clockSVG} />
                <span>{t('Effort', { effort })}</span>
              </div>
              <div className='aiOptimizationsManualCard__info--content-titles-stats-date'>
                <CustomSVGs svg={calendarSVG} />
                <span>{date}</span>
              </div>
            </div>
            {status === 'Snoozed' && snoozedUntil && (
              <div className='aiOptimizationsManualCard__info--content-titles-snooze'>
                <span>{t('SnoozedUntil', { date: snoozedUntil })}</span>
              </div>
            )}
          </div>
        </div>

        <div className='aiOptimizationsManualCard__info--config'>
          {renderButtons()}
        </div>
      </div>
    </div>
  );
};

export default AIOptimizationsManualCard;
